// Comprehensive Help Content for SurFox
// Updated with accurate implementation details from actual codebase

export const helpContent = [
  // ============================================================================
  // QUICK START - Essential First Steps
  // ============================================================================
  {
    id: "a2p-registration",
    category: "quick-start",
    icon: "Shield",
    title: "A2P Registration - Get Started",
    question: "How do I complete A2P registration?",
    answerPreview: "Critical first step: Register your business and campaign with carriers. Takes 3-10 days total.",
    answer: `**🚨 CRITICAL: Complete this FIRST before sending any messages**

A2P (Application-to-Person) registration is mandatory for all business SMS in the US.

## The 4-Step Process

**Step 1: Business Profile** (You fill out form)
- Legal business name
- Business type (LLC, Corporation, Sole Proprietorship)
- EIN or SSN (for sole proprietors)
- Business industry
- Complete address
- Website, email, phone
- Authorized representative information

**Step 2: Await Approval** (5-10 business days)
- Twilio reviews your customer profile
- Check status by clicking "Refresh Status"
- You'll receive email when approved
- If rejected, fix issues and resubmit

**Step 3: Brand Registration** (Automatic after approval)
- SurFox submits your brand to The Campaign Registry (TCR)
- Uses same business info from Step 1
- Approval usually takes 1-3 business days
- Status options: PENDING → IN_REVIEW → VERIFIED

**Step 4: Campaign Setup** (After brand approved)
- Name your campaign (e.g., "Property Seller Outreach")
- Select volume tier (Low Volume Standard recommended)
- Describe how leads opt-in
- Provide 2-3 sample messages
- Acknowledge compliance requirements
- Carrier review: 1-3 hours average

## After Approval
✅ Status shows "VERIFIED"
✅ You can purchase phone numbers
✅ Messages will be delivered
✅ You're fully compliant

## Timeline
Total: **3-10 business days**

## Costs
- Brand: $4/month
- Campaign: $10/month
- Worth it: Messages actually deliver!`,
    steps: [
      { step: 1, text: "Go to Settings → A2P Compliance", action: "Start A2P", link: "/settings", section: "a2p" },
      { step: 2, text: "Fill out complete Business Profile form" },
      { step: 3, text: "Wait 5-10 days, check status regularly" },
      { step: 4, text: "Once approved, register A2P Brand" },
      { step: 5, text: "Complete Campaign Setup form" },
      { step: 6, text: "Wait 1-3 hours for final approval" }
    ],
    quickAction: {
      label: "Start A2P Registration",
      link: "/settings",
      section: "a2p"
    },
    keywords: ["a2p", "registration", "brand", "campaign", "compliance", "setup", "start", "begin", "first", "carrier", "tcpa", "twilio", "approval"],
    relatedIds: ["add-phone-number", "messages-not-sending", "create-campaign"],
    popular: true,
    featured: true,
    popularity: 100
  },

  {
    id: "add-phone-number",
    category: "quick-start",
    icon: "Phone",
    title: "Purchase a Phone Number",
    question: "How do I add a phone number?",
    answerPreview: "Buy a local number for SMS messaging. $1.15/month + usage.",
    answer: `## Prerequisites
✅ A2P campaign status must be **VERIFIED**
✅ Admin permissions required

## Steps to Purchase

**1. Go to Settings → Phone Numbers**

**2. Click "Add Number"** (opens purchase modal)

**3. Select Area Code**
- Choose from popular options (212, 415, 305, 972, 404, 602)
- OR enter custom 3-digit area code
- Click Search to see available numbers

**4. Browse Available Numbers**
- Each shows: formatted number, location, capabilities
- Look for **SMS** and **Voice** badges
- Cost shown: typically $1.15/month

**5. Click "Purchase"**
- Number instantly assigned to your account
- Automatically linked to verified A2P campaign
- Ready to send messages immediately

## After Purchase
- Number appears in your Phone Numbers list
- Status shows "Active"
- **A2P Compliant** badge displayed
- Assign to campaign to start messaging

## Number Features
📱 **SMS Capable** - Send/receive text messages
📞 **Voice Capable** - Make/receive calls
🛡️ **A2P Compliant** - Pre-linked to campaign
📍 **Local** - Better response rates

## Costs
- **Monthly**: $1.15 per number
- **Messages**: $0.0075 per SMS sent
- **Voice**: Additional if used

## Pro Tips
- Local area codes get 20-30% better response
- One number can handle 1,000+ leads
- Numbers cannot be shared between campaigns`,
    steps: [
      { step: 1, text: "Verify A2P status is VERIFIED", action: "Check Status", link: "/settings", section: "a2p" },
      { step: 2, text: "Go to Settings → Phone Numbers", action: "Open Phone Numbers", link: "/settings", section: "phones" },
      { step: 3, text: "Click 'Add Number' button" },
      { step: 4, text: "Select area code and search" },
      { step: 5, text: "Choose number and click Purchase" }
    ],
    quickAction: {
      label: "Purchase Number",
      link: "/settings",
      section: "phones"
    },
    keywords: ["phone", "number", "add", "purchase", "buy", "twilio", "area code", "local", "sms", "get", "acquire"],
    relatedIds: ["a2p-registration", "create-campaign", "messages-not-sending"],
    popular: true,
    popularity: 95
  },

  {
    id: "create-campaign",
    category: "quick-start",
    icon: "Target",
    title: "Create a Campaign",
    question: "How do I create a campaign?",
    answerPreview: "Create a new campaign to organize your leads and messaging.",
    answer: `## Creating a Campaign

Creating a campaign is simple - just give it a name to get started.

### Steps:

**1. Go to Campaign Management**
- Click "Campaigns" in navigation
- Or go to /campaign-management

**2. Click "Create Campaign"**
- Black button in top right

**3. Enter Campaign Name**
- Be descriptive (e.g., "Summer Promotion Q4 2024")
- Examples:
  - "Property Seller Leads - March"
  - "Dental Clinic Follow-Up"
  - "Insurance Renewal Campaign"

**4. Click "Create Campaign"**
- That's it! Campaign is created

### After Creation

Once created, you'll configure the campaign:
- **AI Settings** - Turn on AI, set personality
- **Phone Number** - Assign a number
- **Hot Lead Settings** - Set threshold, assign team member
- **Import Leads** - Upload CSV
- **Start AI** - Activate automated messaging

### Where to Configure

Click on your campaign name to access settings:
- AI Configuration Hub
- Lead import
- Team assignments
- Business hours
- Message templates

### Before You Can Send Messages

⚠️ **Requirements:**
- ✅ A2P registration approved
- ✅ Phone number purchased and assigned
- ✅ AI turned ON
- ✅ Leads imported

### Quick Tips

💡 **Naming Convention:**
Use: [Product/Service] - [Audience] - [Time Period]
Example: "Solar Leads - Homeowners - Q1 2024"

💡 **Start Simple:**
Create campaign → Assign phone → Import 10 test leads → Turn on AI → Monitor → Scale up

💡 **One Campaign Per:**
- Product line
- Target audience
- Geographic area
- Time period`,
    steps: [
      { step: 1, text: "Go to Campaign Management", action: "Open Campaigns", link: "/campaign-management" },
      { step: 2, text: "Click 'Create Campaign' (top right)" },
      { step: 3, text: "Enter campaign name" },
      { step: 4, text: "Click 'Create Campaign' button" },
      { step: 5, text: "Click on campaign to configure settings" }
    ],
    quickAction: {
      label: "Go to Campaigns",
      link: "/campaign-management"
    },
    keywords: ["campaign", "create", "new", "setup", "start", "make", "add"],
    relatedIds: ["upload-leads", "ai-personality", "add-phone-number", "a2p-registration"],
    popular: true,
    popularity: 90
  },

  {
    id: "add-team-member",
    category: "quick-start",
    icon: "Users",
    title: "Invite Team Members",
    question: "How do I add team members?",
    answerPreview: "Invite team members to manage leads and conversations.",
    answer: `## Inviting Team Members

**1. Go to Settings → Team**

**2. Click "Invite User" (top right)**

**3. Fill in Required Information**
- **First Name** - Team member's first name
- **Last Name** - Team member's last name
- **Email** - Where they'll receive the invitation
- **Phone** - For SMS notifications (format: (555) 123-4567)

**4. Select Role**

Choose the appropriate access level:

**User** - Team member with limited access
- View leads and messages
- Respond to conversations
- Add/edit leads manually, import leads
- Tag leads as hot, add notes
- View analytics and AI performance
- **Cannot** change settings, manage team, or access billing

**Business Admin** - Full organizational access
- Everything a User can do PLUS:
- Configure AI settings and personality
- Manage phone numbers
- Edit company profile and business hours
- Invite/remove team members
- Access billing and subscription
- Full control of all settings

**5. Click "Create Invite"**

The team member will:
- Receive an email invitation
- Click the link to create their account
- Set up their password
- Get instant access based on their role

## After Invitation

✅ Track invitation status in the Team section
✅ Resend invitation if needed
✅ Cancel pending invitations anytime
✅ Change roles after they join (Business Admin only)

## Important Notes

⚠️ **Only Business Admins can invite team members**

💡 **Phone number is required** for SMS notifications about hot leads

🔒 **User role recommended for sales team** - gives them access to leads and messages without admin controls`,
    steps: [
      { step: 1, text: "Go to Settings → Team", action: "Open Team", link: "/settings", section: "team" },
      { step: 2, text: "Click 'Invite User' button (top right)" },
      { step: 3, text: "Enter First Name, Last Name, Email, Phone" },
      { step: 4, text: "Select Role (User or Business Admin)" },
      { step: 5, text: "Click 'Create Invite' to send invitation" }
    ],
    quickAction: {
      label: "Invite Team Member",
      link: "/settings",
      section: "team"
    },
    keywords: ["team", "member", "add", "invite", "user", "sales", "rep", "role", "permissions", "access"],
    relatedIds: ["team-roles", "hot-lead-routing", "notifications"],
    popular: true,
    popularity: 85
  },

  // ============================================================================
  // TROUBLESHOOTING - Common Issues
  // ============================================================================
  {
    id: "messages-not-sending",
    category: "troubleshooting",
    icon: "AlertCircle",
    title: "Messages Not Sending",
    question: "Why aren't my messages sending?",
    answerPreview: "Check A2P approval status, campaign settings, and phone number assignment.",
    answer: `## 🔴 MOST COMMON ISSUE: A2P Not Approved

**Check A2P Status First:**
1. Go to Settings → A2P Compliance
2. Look at campaign status
3. Must show **"VERIFIED"** (not PENDING or IN_REVIEW)
4. If not verified → wait for approval or fix rejection

## Diagnostic Checklist

**✓ A2P Registration**
- [ ] Brand status: VERIFIED
- [ ] Campaign status: VERIFIED
- [ ] Profile status: twilio-approved

**✓ Campaign Settings**
- [ ] Campaign is Active (not paused)
- [ ] AI toggle is ON
- [ ] Phone number is assigned
- [ ] Business hours configured correctly

**✓ Phone Number**
- [ ] Number status: Active
- [ ] Assigned to this campaign
- [ ] Has "A2P Compliant" badge
- [ ] Not released or transferred

**✓ Time/Schedule**
- [ ] Current time within business hours
- [ ] Not outside configured days
- [ ] Time zone set correctly
- [ ] OR 24/7 mode enabled

**✓ Throttling**
- [ ] Not exceeding 120 msgs/hour
- [ ] Campaign daily limit not reached
- [ ] Account in good standing

## Quick Test
**Text your campaign phone number yourself**
- You should get auto-response in 30 seconds
- If no response → issue confirmed
- If response works → check lead phone formatting

## Common Fixes

**A2P Pending** → Wait or contact support
**Campaign Paused** → Toggle to Active
**Wrong Hours** → Adjust in Settings → Messaging
**No Phone** → Assign in Campaign Settings
**Number Issues** → Go to Settings → Phone Numbers

## Still Not Working?
1. Click "Refresh Status" in A2P settings
2. Check Control Room for error messages
3. Verify lead phone numbers in E.164 format (+1XXXXXXXXXX)
4. Contact support with campaign ID`,
    steps: [
      { step: 1, text: "Check A2P status = VERIFIED", action: "Check A2P", link: "/settings", section: "a2p" },
      { step: 2, text: "Verify campaign is Active with AI ON" },
      { step: 3, text: "Confirm phone number assigned" },
      { step: 4, text: "Check business hours settings" },
      { step: 5, text: "Test by texting your number" }
    ],
    quickAction: {
      label: "Check System Status",
      link: "/control-room"
    },
    keywords: ["messages", "not sending", "failed", "error", "broken", "not working", "delivery", "won't send", "can't send", "unable"],
    relatedIds: ["a2p-registration", "campaign-status", "phone-number-issues"],
    popular: true,
    featured: true,
    popularity: 98
  },

  {
    id: "no-hot-leads",
    category: "troubleshooting",
    icon: "Flame",
    title: "Not Getting Hot Leads",
    question: "Why aren't I getting any hot leads?",
    answerPreview: "Threshold may be too high or leads aren't engaging enough.",
    answer: `## Understanding Hot Lead Scoring

**Hot Lead Criteria (Score 0-100):**
- Response speed (+points)
- Message length (+points)
- Positive sentiment (+points)
- Buying signals: "how much", "when", "interested" (+points)
- Questions about process (+points)
- Urgency indicators (+points)
- Default threshold: **75+** = Hot

## Common Reasons for No Hot Leads

**1. Threshold Too High** (Most Common)
- Default 75 may be too strict
- **Solution**: Lower to 65-70
- Go to Settings → AI & Automation
- Adjust "Escalation Threshold"
- Monitor for 24-48 hours

**2. Leads Not Responding**
- Check response rate (should be 15-25%)
- If under 10% → lead quality issue
- **Fix**: Improve opening message or lead source

**3. Wrong AI Personality**
- Professional → B2B, formal industries
- Friendly → Real estate, local services
- Casual → Younger demographics
- **Test**: Switch and compare

**4. Conversations Too Short**
- Leads might be interested but not enough messages
- Check average messages per lead (need 4-6)
- **Fix**: Increase conversation limit

**5. Scoring Algorithm Needs Tuning**
- Some industries have different signals
- **Solution**: Customize scoring rules
- Settings → AI & Automation → Scoring

## Quick Wins

**Immediate Actions:**
1. Lower threshold to 65
2. Review last 10 conversations manually
3. Look for patterns (are they interested but not "hot"?)
4. Adjust opening message to elicit stronger responses
5. A/B test different personalities

**Monitor These Metrics:**
- Response rate (target: 20%+)
- Avg messages per lead (target: 5+)
- Positive sentiment % (target: 60%+)
- Conversation completion (target: 40%+)

## Expected Results
After adjustments, you should see:
- 10-15% of responding leads → Hot
- At least 1-2 hot leads per 100 messaged
- If still zero → review lead quality`,
    steps: [
      { step: 1, text: "Go to Settings → AI & Automation", action: "AI Settings", link: "/settings", section: "ai" },
      { step: 2, text: "Lower threshold to 65-70" },
      { step: 3, text: "Review recent conversations" },
      { step: 4, text: "Check response rate (need 15%+)" },
      { step: 5, text: "Test different AI personality" },
      { step: 6, text: "Monitor for 24-48 hours" }
    ],
    quickAction: {
      label: "Adjust AI Settings",
      link: "/settings",
      section: "ai"
    },
    keywords: ["hot leads", "not getting", "no leads", "zero", "none", "threshold", "escalation", "scoring"],
    relatedIds: ["hot-lead-threshold", "ai-personality", "lead-scoring"],
    popular: true,
    popularity: 92
  },

  {
    id: "high-unsubscribe",
    category: "troubleshooting",
    icon: "XCircle",
    title: "High Unsubscribe Rate",
    question: "Why do leads keep opting out?",
    answerPreview: "Aggressive messaging, poor lead quality, or timing issues.",
    answer: `## ⚠️ Target Unsubscribe Rate: Under 2%

If you're above 3%, take immediate action.

## Common Causes & Fixes

**1. Aggressive Opening Message**
❌ Bad: "Hey! Want to sell your house for cash TODAY?"
✅ Good: "Hi [Name], saw your interest in selling. Got a quick question?"

**Fix:**
- Soften language
- Add value first
- Ask questions
- Be conversational

**2. Poor Lead Quality**
❌ Purchased/scraped lists
❌ No consent to SMS
❌ Wrong target audience

**Fix:**
- Only message leads with explicit consent
- Verify opt-in source
- Update lead capture forms with clear SMS disclosure

**3. Messaging Outside Hours**
❌ Texting at 8 PM or weekends
✅ 9 AM - 6 PM Monday-Friday

**Fix:**
- Settings → Messaging → Business Hours
- Set appropriate window
- Respect local time zones

**4. Too Frequent Messages**
❌ Multiple messages per day
✅ Max 1 per day, 3-4 per week

**Fix:**
- Increase follow-up delays in campaign settings
- Space out follow-ups
- Reduce frequency

**5. Misleading Opt-In**
❌ Hidden SMS consent in fine print
✅ Clear checkbox: "I agree to receive SMS"

**Fix:**
- Update lead capture forms
- Make SMS consent explicit
- Send confirmation message

**6. Not Providing Value**
❌ Just asking for sale
✅ Offering help, info, resources

**Fix:**
- Educate first
- Build relationship
- Solve problems

## Emergency Protocol (>3% Opt-Out)

**Immediate Actions:**
1. **PAUSE** campaign immediately
2. Review and rewrite all messaging
3. Verify 100% of leads consented
4. Reduce frequency to max 1/day
5. A/B test softer approach
6. Monitor closely for 48 hours

## Monitoring
Check these daily:
- Opt-out rate (should be <2%)
- Response rate (should be >15%)
- Complaint rate (should be <0.1%)
- Hot lead conversion

## Compliance Note
High opt-out rates can trigger:
- Carrier filtering
- A2P campaign suspension
- Phone number blocking
- Account review

Keep it under 2% to stay safe.`,
    steps: [
      { step: 1, text: "Check current opt-out rate in Analytics" },
      { step: 2, text: "Review opening message - soften if needed" },
      { step: 3, text: "Verify all leads opted in with consent" },
      { step: 4, text: "Adjust business hours", action: "Messaging Settings", link: "/settings", section: "messaging" },
      { step: 5, text: "Reduce message frequency" },
      { step: 6, text: "A/B test new approach" }
    ],
    quickAction: {
      label: "Review Messaging Settings",
      link: "/settings",
      section: "messaging"
    },
    keywords: ["unsubscribe", "opt out", "high rate", "stop", "spam", "complaints", "too many"],
    relatedIds: ["messaging-best-practices", "business-hours", "lead-quality"],
    popular: true,
    popularity: 80
  },

  // ============================================================================
  // FEATURES - Understanding the Platform
  // ============================================================================
  {
    id: "what-is-hot-lead",
    category: "features",
    icon: "Flame",
    title: "What is a Hot Lead?",
    question: "What makes a lead 'hot'?",
    answerPreview: "A lead showing strong buying signals and high engagement, ready for immediate sales contact.",
    answer: `## Hot Lead Definition

A **Hot Lead** is a prospect the AI has identified as sales-ready based on their conversation behavior and engagement level.

## How AI Scores Leads (0-100)

**Positive Signals (Increase Score):**
- ⚡ **Fast responses** - Replies within minutes
- 📝 **Detailed messages** - Multiple sentences
- 😊 **Positive sentiment** - Enthusiastic language
- 💰 **Buying signals**:
  - "how much"
  - "what's the cost"
  - "when can we start"
  - "I'm interested"
  - "send me information"
- ⏰ **Urgency indicators**:
  - "ASAP"
  - "soon"
  - "by next week"
  - "need this now"
- ❓ **Questions** - Asking about process, timeline, pricing

**Negative Signals (Decrease Score):**
- 🤔 Hesitation: "not sure", "maybe"
- 😐 Short responses: "ok", "k"
- 🐌 Slow replies (hours/days)
- ❌ Negative sentiment
- 🚫 "I'll think about it"

## Score Ranges

**🔥 80-100: HOT**
- Immediate sales contact required
- Call within 15 minutes
- High conversion probability

**🌶️ 60-79: WARM**
- Good engagement
- Follow up soon
- Nurture to hot

**☀️ 40-59: ACTIVE**
- Some interest shown
- Continue conversation
- Watch for signals

**❄️ 0-39: COLD**
- Minimal engagement
- Low priority
- May need re-engagement

## What Happens When Lead Goes Hot?

**Automatic Actions:**
1. Status changes to "Hot Lead"
2. Assigned sales rep gets notified (email + SMS)
3. Lead appears in Hot Lead Queue
4. AI continues conversation but notifies rep
5. Rep should call within 15 minutes

## Configuring Hot Lead Behavior

**Settings → AI & Automation**

**Escalation Threshold** (Default: 75)
- Higher = Fewer but stronger leads
- Lower = More leads, varying quality
- Recommended: 70-80

**Assignment Rules**
- Round robin to team
- Specific rep per campaign
- By availability
- Manual assignment

**Notifications**
- Email alerts
- SMS notifications
- In-app notifications
- Daily summaries

## Best Practices

🎯 **Act Fast** - Call hot leads within 15 min
📞 **Call, Don't Text** - Voice shows commitment
📊 **Track Conversion** - Measure hot → closed
🔄 **Adjust Threshold** - Based on results
👥 **Assign Wisely** - Best reps for hot leads

## Monitoring

Check these metrics:
- Hot lead volume (should be 5-10% of responses)
- Hot → appointment rate (target: 40%+)
- Hot → closed rate (target: 15%+)
- Time to first contact (target: <15 min)`,
    keywords: ["hot lead", "what is", "definition", "scoring", "threshold", "escalation", "sales ready"],
    relatedIds: ["hot-lead-threshold", "lead-scoring", "ai-features"],
    popular: true,
    popularity: 88
  },

  {
    id: "ai-personality",
    category: "features",
    icon: "Brain",
    title: "AI Tone & Persona Settings",
    question: "How do I configure AI tone and persona?",
    answerPreview: "Control AI communication style with tone and persona combinations for each conversation stage.",
    answer: `## How AI Personality Works

SurFox uses a sophisticated **Tone + Persona** system that lets you customize AI behavior at different stages:

1. **Initial Contact** - First message to new leads
2. **Engagement** - Ongoing conversation with interested leads
3. **Follow-ups** - Automated messages for re-engagement

Each stage has separate Tone + Persona settings for maximum control.

## Where to Configure

**Go to:** Settings → AI Instructions

You'll see three sections:
- Initial Contact Instructions (blue)
- Engagement Instructions (green)
- Follow-up Sequence (purple)

## 5 Tone Options

Choose the communication style:

**1. Friendly & Casual**
- Warm, approachable, conversational
- Uses first names, friendly language
- Best for: Real estate, local services, consumer products
- Example: "Hey John! Quick question about your home..."

**2. Neutral & Professional**
- Formal, respectful, business-focused
- Uses industry terminology appropriately
- Best for: B2B, enterprise, financial services
- Example: "Good morning, John. I'm reaching out regarding..."

**3. Assertive & Confident**
- Direct, decisive, action-oriented
- Clear calls-to-action, urgency
- Best for: Sales, high-velocity outreach
- Example: "John - saw your inquiry. Let's schedule 15 minutes..."

**4. Empathetic & Supportive**
- Understanding, patient, helpful
- Focuses on problem-solving
- Best for: Healthcare, customer service, education
- Example: "Hi John, I understand this can be overwhelming..."

**5. Aggressive & Bold**
- Very direct, high-pressure, urgent
- Strong language, immediate action
- Best for: Limited offers, time-sensitive deals
- Example: "John - opportunity expires tonight..."

## Persona Options

Define AI's role in the conversation:

**Initial Personas:**
- Icebreaker / Intro - Start friendly conversation
- Lead Qualifier - Ask qualifying questions
- FAQ Assistant - Answer common questions
- Information Gatherer - Collect lead details
- First Contact Specialist - Professional introduction

**Engagement Personas:**
- Nurturer - Build relationship over time
- Appointment Setter - Schedule meetings
- Closer - Drive to conversion
- Relationship Builder - Long-term engagement
- Follow-up Specialist - Persistent re-engagement
- Objection Handler - Address concerns

**Follow-up Personas:**
- Gentle Reminder - Soft re-engagement
- Value Provider - Share helpful information
- Urgency Creator - Time-sensitive messaging
- Closing Specialist - Final conversion push
- Final Attempt - Last touchpoint

## Configuration Example

**Real Estate Example:**

**Initial Contact:**
- Tone: Friendly & Casual
- Persona: Icebreaker / Intro

**Engagement:**
- Tone: Friendly & Casual
- Persona: Appointment Setter

**Follow-up #1 (Day 3):**
- Tone: Friendly & Casual
- Persona: Gentle Reminder

**Follow-up #2 (Day 7):**
- Tone: Assertive & Confident
- Persona: Urgency Creator

## Best Practices

✅ **Match your industry** - Professional for B2B, Friendly for consumer

✅ **Stay consistent** - Don't change tone dramatically mid-conversation

✅ **Test combinations** - Different tone/persona combos for different audiences

✅ **Monitor results** - Check response rates after changes

## Where This Applies

- These settings are **global** across all campaigns
- Changes affect new conversations immediately
- Existing conversations continue with original settings
- Re-build AI bundle after changes to activate`,
    keywords: ["ai", "personality", "tone", "persona", "friendly", "professional", "casual", "assertive", "empathetic", "voice", "style", "instructions"],
    relatedIds: ["create-campaign", "ai-settings"],
    popular: true,
    popularity: 78
  },

  {
    id: "lead-import",
    category: "features",
    icon: "Upload",
    title: "Import Leads from CSV",
    question: "How do I upload leads?",
    answerPreview: "Import leads via CSV file with proper field mapping.",
    answer: `## CSV Upload Process

**1. Prepare Your CSV File**

**Required Columns (Choose ONE name format):**

Option A - Separate Names:
- \`first_name\` - Lead's first name
- \`last_name\` - Lead's last name
- \`phone\` - Phone number (any format, will be cleaned)

Option B - Full Name:
- \`name\` or \`full_name\` - Full name (will be split into first/last)
- \`phone\` - Phone number (any format, will be cleaned)

**Optional Columns:**
- \`email\` - Email address
- \`address\` - Property/mailing address
- \`city\` - City
- \`state\` - State (2-letter code)
- \`zip\` - ZIP code
- \`property_type\` - Type of property
- \`estimated_value\` - Property value
- \`lead_source\` - Where lead came from
- \`notes\` - Additional context
- Any custom fields you created

**Example CSV:**
\`\`\`
first_name,last_name,phone,email,address,city,state,zip
John,Smith,555-123-4567,john@email.com,123 Main St,Miami,FL,33101
Sarah,Johnson,(555) 234-5678,sarah@email.com,456 Oak Ave,Orlando,FL,32801
\`\`\`

**2. Go to Dashboard → Click "Add Lead"**

**3. Click "AI-Powered Import" Tab**

**4. Upload CSV**
- Drag and drop your CSV file
- Or click to browse and select
- File uploads and system auto-detects fields

**5. Map Fields**
- System intelligently auto-detects common columns (phone, name, email)
- Review auto-mapped fields (highlighted in blue)
- Manually map any remaining columns if needed
- Preview shows first 5 rows of data

**6. Complete Mapping**
- Verify Phone and Name fields are mapped (required!)
- Click "Complete Mapping" button

**7. Select Campaign & Import**
- Choose which campaign to import leads into
- Review lead count and mapped fields
- Click "Import [X] Leads" button

**8. Processing**
- Import job is queued in the background
- System validates phone numbers
- Converts to E.164 format (+1XXXXXXXXXX)
- Checks for duplicates within campaign
- Assigns all leads to selected campaign

**9. Monitor Progress**
- Switch to "Import Status" tab to track progress
- See real-time updates on processing
- View successful imports and any errors
- Get final count of imported vs. skipped leads

## Phone Number Formats Accepted

All these work (converted to +1XXXXXXXXXX):
- 5551234567
- 555-123-4567
- (555) 123-4567
- +1 555 123 4567
- 1-555-123-4567

**Important:** Must be valid US/Canada numbers

## Common Errors

**"Phone and Name fields are required"**
- You must map the Phone field
- AND either Full Name OR both First Name and Last Name
- **Fix**: Go back and complete field mapping

**"Invalid phone number"**
- Not 10 digits
- Invalid area code
- International (non-US/Canada)
- **Fix**: Correct in CSV and re-upload

**"Duplicate detected"**
- Lead with same phone already exists in that campaign
- **Result**: Skipped (not imported)

**"No campaign selected"**
- Import button disabled
- **Fix**: Select a campaign from dropdown (or create new campaign)

## Best Practices

✅ **Clean Data First**
- Remove duplicates before upload
- Validate phone numbers
- Standardize formatting

✅ **Start Small**
- Test with 10-20 leads first
- Verify everything works
- Then upload full list

✅ **Custom Fields**
- Use for industry-specific data
- Helps with personalization
- Improves AI context

✅ **Lead Source Tracking**
- Track where leads came from
- Analyze which sources convert
- Optimize acquisition

## After Import

**What Happens Next:**
1. Leads status: "New"
2. AI begins outreach (during business hours)
3. Sends opening message
4. Tracks all responses
5. Scores engagement
6. Escalates hot leads

**Timeline:**
- Import: Immediate
- First message: Within business hours
- AI response: Real-time to replies
- Hot lead notification: When threshold hit`,
    keywords: ["import", "upload", "csv", "leads", "bulk", "add", "file"],
    relatedIds: ["create-campaign", "lead-management", "phone-format"],
    popular: true,
    popularity: 70
  },

  // ============================================================================
  // SETTINGS - Configuration Help
  // ============================================================================
  {
    id: "business-hours",
    category: "settings",
    icon: "Clock",
    title: "Configure Business Hours",
    question: "How do I set when messages send?",
    answerPreview: "Control when AI sends outbound messages to avoid texting at bad times.",
    answer: `## Setting Business Hours

**Purpose:** Prevent sending messages at inappropriate times that cause opt-outs

**1. Go to Settings → Messaging**

**2. Configure Hours**

**Start Time**
- Recommended: 9:00 AM local time
- Earliest: 8:00 AM (legal)
- Never before 8 AM (TCPA violation)

**End Time**
- Recommended: 6:00 PM local time
- Latest: 9:00 PM (legal)
- Never after 9 PM (TCPA violation)

**Active Days**
- **B2B**: Monday - Friday only
- **B2C**: Monday - Friday (sometimes Saturday)
- **E-commerce**: 7 days/week (with caution)

**Time Zone**
- Set to YOUR business time zone
- Or lead's time zone if different
- System respects this strictly

**3. AI Behavior Within Hours**

**What Happens:**
- ✅ Sends opening messages to new leads
- ✅ Sends follow-ups to existing conversations
- ✅ Processes scheduled messages
- ✅ Continues active conversations

**4. AI Behavior Outside Hours**

**What Happens:**
- ⏸️ Queues new messages for next business day
- ⏸️ Holds scheduled follow-ups
- ✅ STILL responds to inbound messages (24/7)
- ✅ Lead scores still update
- ✅ Hot lead notifications still sent

**Why respond 24/7?**
If lead texts you at 8 PM, they expect response.
Not responding = lost lead.

**5. 24/7 Mode**

**When to Use:**
- Emergency services
- Global audiences (multiple time zones)
- E-commerce with international customers
- Healthcare
- On-call services

**Caution:**
- Higher opt-out rates
- May seem intrusive
- Test carefully

## Recommended Settings by Industry

**Real Estate (B2C)**
- Mon-Fri: 9 AM - 7 PM
- Sat: 10 AM - 4 PM
- Sun: Off

**Real Estate (B2B/Investors)**
- Mon-Fri: 9 AM - 6 PM
- Weekends: Off

**Local Services**
- Mon-Fri: 9 AM - 6 PM
- Sat: 10 AM - 3 PM
- Sun: Off

**SaaS/B2B**
- Mon-Fri: 9 AM - 5 PM
- Weekends: Off

**E-commerce**
- 7 days: 9 AM - 8 PM

**Healthcare**
- Mon-Fri: 8 AM - 6 PM
- Emergency: 24/7 mode

## Impact on Results

**Good Hours = Better Results:**
- Lower opt-out rates
- Higher response rates
- More positive sentiment
- Better compliance

**Poor Hours = Problems:**
- High opt-outs (3-5%+)
- Complaints
- Negative sentiment
- Risk of carrier filtering

## Testing Different Hours

**A/B Test Process:**
1. Split campaigns by hours
2. Campaign A: 9 AM - 6 PM
3. Campaign B: 9 AM - 8 PM
4. Run for 2 weeks
5. Compare:
   - Response rate
   - Opt-out rate
   - Hot lead %
   - Complaints

## Legal Compliance

**TCPA Requirements:**
- No calls/texts before 8 AM
- No calls/texts after 9 PM
- Must respect lead's local time
- Violations: $500-$1,500 per message

**SurFox Protection:**
- Automatically blocks outside 8 AM - 9 PM
- Warns if hours too aggressive
- Tracks compliance

## Override Options

**Manual Override:**
- Send individual messages anytime
- For urgent follow-ups
- Use sparingly
- Recipient must be expecting it`,
    steps: [
      { step: 1, text: "Go to Settings → Messaging", action: "Open Settings", link: "/settings", section: "messaging" },
      { step: 2, text: "Set start time (9 AM recommended)" },
      { step: 3, text: "Set end time (6 PM recommended)" },
      { step: 4, text: "Select active days (Mon-Fri typical)" },
      { step: 5, text: "Confirm time zone" },
      { step: 6, text: "Save changes" }
    ],
    quickAction: {
      label: "Configure Hours",
      link: "/settings",
      section: "messaging"
    },
    keywords: ["business hours", "time", "schedule", "when", "texting hours", "operating hours", "availability"],
    relatedIds: ["messaging-settings", "compliance", "high-unsubscribe"],
    popular: true,
    popularity: 75
  }
];

export default helpContent;
